import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Create a workspace (Phase 3.2 / 3.3).
 *
 * ── The contract ────────────────────────────────────────────────────────────
 *   POST /api/workspaces  { name }
 *     201 { workspace_id, name, plan }
 *     403 { detail: { feature_disabled, feature } }   flag went off under us
 *     409 { detail: { limit_reached, limit, plan } }  at the workspace cap
 *
 * ── Why this creates AND switches ───────────────────────────────────────────
 * 201 tells us a workspace now exists. It does NOT, on its own, tell us the
 * session is seated in it. If we navigated to /onboarding on a 201 alone and the
 * server had left the old workspace active, the user would generate and confirm a
 * brand into the WRONG TENANT — the exact cross-tenant write we have already
 * fixed twice in this flow. So we make seating explicit: POST /workspaces/switch
 * with the new id before we go anywhere. It is a no-op if the server already
 * seated us, and it is the difference between "correct" and "correct by luck" if
 * it did not.
 *
 * If the switch fails we do NOT navigate. The workspace exists (it will appear in
 * the switcher) but we are not in it, and onboarding under the old workspace is
 * precisely the thing we are avoiding.
 *
 * ── The full page load is gone (Phase 5.9) ──────────────────────────────────
 * It was here for the same reason it was in WorkspaceSwitcher: every
 * workspace-scoped fetch was a mount-time effect with `[]` deps, and only a
 * remount refreshed them. Both files are converted together, as the note that
 * used to sit here said they must be.
 *
 * The seating is now done by `switchWorkspace` from AuthContext rather than a
 * bare POST. That is not a refactor for tidiness: `switchWorkspace` is what
 * clears the previous tenant, pins `X-Workspace-Id` to the new one and bumps
 * the epoch. A bare POST followed by a soft navigate would leave every screen
 * holding the OLD workspace's data — which is precisely the failure the reload
 * was covering for, and it would be silent.
 *
 * ── Onboarding drafts ───────────────────────────────────────────────────────
 * Nothing to clear here. Draft storage is namespaced per workspace
 * (lib/brandDraft.js) and the new workspace has an id no draft has ever used, so
 * a stale draft from another workspace is unreadable from this one by
 * construction. Verified by driving it, not assumed.
 *
 * ── COPY ────────────────────────────────────────────────────────────────────
 * Every user-facing string below is marked `COPY SLOT`. Each one is wired and
 * true, but the WORDING is provisional — it was not written by Ori. The slot
 * names are listed in the handoff so they can be swapped one for one.
 */

const MAX_NAME = 60;

export default function NewWorkspaceDialog({ open, onClose, onFeatureDisabled }) {
  const navigate = useNavigate();
  const { switchWorkspace } = useAuth();
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // `blocked` holds the at-cap state. It is deliberately separate from a generic
  // error: it is not a failure the user can retry away, and it must survive them
  // editing the name field.
  const [blocked, setBlocked] = useState(null); // { limit, plan }
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setName("");
    setSubmitting(false);
    setBlocked(null);
    setError(null);
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, submitting, onClose]);

  if (!open) return null;

  const trimmed = name.trim();
  const tooLong = trimmed.length > MAX_NAME;
  const canSubmit = trimmed.length > 0 && !tooLong && !submitting && !blocked;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);

    let created;
    try {
      created = await api.post("/workspaces", { name: trimmed }).then((r) => r.data);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // eslint-disable-next-line no-console
      console.error("[workspaces/create] failed", status, detail);

      if (status === 403 && detail?.feature_disabled) {
        // The flag went off between page load and this click. Do not explain a
        // feature that no longer exists — take the action away and close.
        onFeatureDisabled?.();
        onClose?.();
        return;
      }
      if (status === 409 && detail?.limit_reached) {
        setBlocked({ limit: detail.limit, plan: detail.plan });
        setSubmitting(false);
        return;
      }
      // COPY SLOT ws.create.error.generic — provisional, Ori to write
      setError("That didn't work. Try again.");
      setSubmitting(false);
      return;
    }

    const newId = created?.workspace_id;
    if (!newId) {
      // eslint-disable-next-line no-console
      console.error("[workspaces/create] 201 with no workspace_id", created);
      // COPY SLOT ws.create.error.generic — provisional, Ori to write
      setError("That didn't work. Try again.");
      setSubmitting(false);
      return;
    }

    try {
      await switchWorkspace(newId);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[workspaces/create] switch after create failed", err?.response?.status);
      // The workspace exists; we are not seated in it. Staying put is the safe
      // outcome — onboarding in the previous workspace would write the new
      // brand into the wrong tenant.
      // COPY SLOT ws.create.error.switch — provisional, Ori to write
      setError("Your workspace was created, but we couldn't open it. Pick it from the list.");
      setSubmitting(false);
      return;
    }

    // 3.3 — a new workspace has no brand. The dashboard would be a dead end.
    // Keep `submitting` true: the navigation is the terminal state.
    //
    // Safe as a SOFT navigate only because `switchWorkspace` has already
    // resolved above: by the time this line runs the context holds the new
    // workspace, the epoch has moved, and Onboarding's own reset effect has a
    // clean tenant to mount against.
    onClose?.();
    navigate("/onboarding");
  };

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40"
        onPointerDown={() => !submitting && onClose?.()}
        aria-hidden
      />
      <form
        onSubmit={submit}
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-workspace-title"
        data-testid="new-workspace-dialog"
        className="relative w-full max-w-md bg-lm-surface border border-lm-border rounded-2xl shadow-[0_24px_60px_-16px_rgba(0,0,0,0.35)] p-6 animate-[fadeIn_0.15s_ease]"
      >
        <button
          type="button"
          onClick={() => !submitting && onClose?.()}
          data-testid="new-workspace-close"
          aria-label="Close"
          className="absolute top-4 right-4 text-lm-text3 hover:text-lm-text transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        {/* COPY SLOT ws.create.title — provisional, Ori to write */}
        <h2
          id="new-workspace-title"
          className="font-display font-bold text-lg text-lm-text pr-6"
        >
          New workspace
        </h2>

        {blocked ? (
          <>
            {/* COPY SLOT ws.create.limit — provisional, Ori to write. Renders the REAL limit
                and plan from the 409; no invented number. */}
            <p
              data-testid="new-workspace-limit"
              className="mt-3 text-sm text-lm-text2 leading-relaxed"
            >
              You're using all {blocked.limit} workspace
              {blocked.limit === 1 ? "" : "s"} your plan includes.
            </p>
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => onClose?.()}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-lm-tint text-lm-text hover:bg-lm-tint2 transition-colors"
              >
                {/* COPY SLOT ws.create.limit.dismiss — provisional, Ori to write */}
                Close
              </button>
            </div>
          </>
        ) : (
          <>
            {/* COPY SLOT ws.create.field.label — provisional, Ori to write */}
            <label
              htmlFor="new-workspace-name"
              className="block mt-4 text-sm font-medium text-lm-text2"
            >
              Name
            </label>
            <input
              id="new-workspace-name"
              ref={inputRef}
              data-testid="new-workspace-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={MAX_NAME}
              disabled={submitting}
              autoComplete="off"
              className="mt-1.5 w-full rounded-lg border border-lm-border bg-lm-bg px-3 py-2 text-sm text-lm-text outline-none focus:border-lm-primary focus:ring-2 focus:ring-lm-primary/15 disabled:opacity-60"
            />

            {error ? (
              <p
                data-testid="new-workspace-error"
                role="alert"
                className="mt-2 text-sm text-red-600"
              >
                {error}
              </p>
            ) : null}

            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => onClose?.()}
                disabled={submitting}
                className="px-4 py-2 rounded-lg text-sm font-medium text-lm-text2 hover:bg-lm-tint transition-colors disabled:opacity-60"
              >
                {/* COPY SLOT ws.create.cancel — provisional, Ori to write */}
                Cancel
              </button>
              <button
                type="submit"
                disabled={!canSubmit}
                data-testid="new-workspace-submit"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-lm-primary text-white hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                {/* COPY SLOT ws.create.submit — provisional, Ori to write */}
                Create
              </button>
            </div>
          </>
        )}
      </form>
    </div>,
    document.body
  );
}
